import type { AssetSearchResult } from '@einhaus/project314-types';
import { Button, FormNumberInput, FormSelect } from '@/components/UI';
import InstrumentPicker from './InstrumentPicker';
import {
    actionChoices,
    biasChoices,
    formChoices,
    horizonChoices,
    instrumentKey,
    lifecycleChoices,
    optionTypeChoices,
    positionSideChoices,
    statementKindChoices,
    targetRoleChoices,
    type DraftCall,
    type InstrumentDisplay,
    type LabelledTarget
} from './callReviewModel';

type CallLabelEditorProps = {
    calls: DraftCall[];
    instruments: Map<string, InstrumentDisplay>;
    /** Keys of calls added by hand that still need an instrument. */
    unpicked: Set<string>;
    onChange: (calls: DraftCall[]) => void;
    onInstrumentPicked: (call: DraftCall, result: AssetSearchResult) => void;
    onRemove: (call: DraftCall) => void;
};

/** The form for the calls a post really contains; each card is one call. */
const CallLabelEditor = ({ calls, instruments, unpicked, onChange, onInstrumentPicked, onRemove }: CallLabelEditorProps) => {
    const update = (key: string, patch: Partial<DraftCall>) =>
        onChange(calls.map((call) => (call.key === key ? { ...call, ...patch } : call)));
    const updateTarget = (call: DraftCall, index: number, patch: Partial<LabelledTarget>) =>
        update(call.key, { targets: call.targets.map((target, position) => (position === index ? { ...target, ...patch } : target)) });

    return (
        <div className="space-y-4">
            {calls.map((call, index) => (
                <section className="rounded-lg border border-slate-700 bg-slate-900/60 p-4" key={call.key}>
                    <div className="mb-3 flex items-start justify-between gap-3">
                        <div className="space-y-1">
                            <p className="text-xs uppercase tracking-wide text-slate-500">Call {index + 1}</p>
                            <InstrumentPicker
                                display={unpicked.has(call.key) ? undefined : instruments.get(instrumentKey(call))}
                                onPick={(result) => onInstrumentPicked(call, result)}
                                startOpen={unpicked.has(call.key)}
                            />
                        </div>
                        <Button onClick={() => onRemove(call)} size="sm" variant="danger">
                            Remove
                        </Button>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <FormSelect
                            label="Statement"
                            noMargin
                            onChange={(event) => update(call.key, { statementKind: event.target.value as DraftCall['statementKind'] })}
                            options={statementKindChoices}
                            value={call.statementKind}
                        />
                        <FormSelect
                            label="Direction (view)"
                            noMargin
                            onChange={(event) =>
                                update(call.key, { underlyingBias: (event.target.value || null) as DraftCall['underlyingBias'] })
                            }
                            options={biasChoices}
                            value={call.underlyingBias ?? ''}
                        />
                        <FormSelect
                            label="Position the author claims"
                            noMargin
                            onChange={(event) =>
                                update(call.key, { positionSide: (event.target.value || null) as DraftCall['positionSide'] })
                            }
                            options={positionSideChoices}
                            value={call.positionSide ?? ''}
                        />
                        <FormSelect
                            label="Action"
                            noMargin
                            onChange={(event) => update(call.key, { action: event.target.value as DraftCall['action'] })}
                            options={actionChoices}
                            value={call.action}
                        />
                        <FormSelect
                            label="Instrument type"
                            noMargin
                            onChange={(event) => {
                                const instrumentForm = event.target.value as DraftCall['instrumentForm'];
                                update(call.key, {
                                    instrumentForm,
                                    optionType: instrumentForm === 'option' ? (call.optionType ?? 'call') : null
                                });
                            }}
                            options={formChoices}
                            value={call.instrumentForm}
                        />
                        {call.instrumentForm === 'option' && (
                            <FormSelect
                                label="Option"
                                noMargin
                                onChange={(event) =>
                                    update(call.key, { optionType: event.target.value as NonNullable<DraftCall['optionType']> })
                                }
                                options={optionTypeChoices}
                                value={call.optionType ?? 'call'}
                            />
                        )}
                        <FormSelect
                            label="Timeframe"
                            noMargin
                            onChange={(event) => update(call.key, { horizonClass: event.target.value as DraftCall['horizonClass'] })}
                            options={horizonChoices}
                            value={call.horizonClass}
                        />
                        <FormSelect
                            label="Change to an earlier call"
                            noMargin
                            onChange={(event) => update(call.key, { statedLifecycle: event.target.value as DraftCall['statedLifecycle'] })}
                            options={lifecycleChoices}
                            value={call.statedLifecycle}
                        />
                        <FormNumberInput
                            helpText="Only a price the author says they opened at"
                            label="Entry price"
                            min={0}
                            noMargin
                            onValueChange={(claimedEntryPrice) => update(call.key, { claimedEntryPrice })}
                            prefix="$"
                            value={call.claimedEntryPrice}
                        />
                    </div>

                    <div className="mt-4 space-y-2">
                        <p className="text-sm text-slate-300">Price levels the author states</p>
                        {call.targets.map((target, targetIndex) => (
                            <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]" key={`${call.key}-target-${targetIndex}`}>
                                <FormSelect
                                    label="Kind"
                                    noMargin
                                    onChange={(event) =>
                                        updateTarget(call, targetIndex, { targetRole: event.target.value as LabelledTarget['targetRole'] })
                                    }
                                    options={targetRoleChoices}
                                    value={target.targetRole}
                                />
                                <FormNumberInput
                                    label="Low (or the level)"
                                    min={0}
                                    noMargin
                                    onValueChange={(priceLow) =>
                                        updateTarget(call, targetIndex, {
                                            priceLow: priceLow ?? 0,
                                            priceHigh: target.priceHigh === target.priceLow ? (priceLow ?? 0) : target.priceHigh
                                        })
                                    }
                                    value={target.priceLow || null}
                                />
                                <FormNumberInput
                                    label="High (same for one level)"
                                    min={0}
                                    noMargin
                                    onValueChange={(priceHigh) => updateTarget(call, targetIndex, { priceHigh: priceHigh ?? 0 })}
                                    value={target.priceHigh || null}
                                />
                                <Button
                                    onClick={() =>
                                        update(call.key, { targets: call.targets.filter((_, position) => position !== targetIndex) })
                                    }
                                    size="sm"
                                    variant="secondary"
                                >
                                    Remove level
                                </Button>
                            </div>
                        ))}
                        <Button
                            onClick={() =>
                                update(call.key, { targets: [...call.targets, { targetRole: 'price_target', priceLow: 0, priceHigh: 0 }] })
                            }
                            size="sm"
                            variant="secondary"
                        >
                            Add level
                        </Button>
                    </div>
                </section>
            ))}
        </div>
    );
};

export default CallLabelEditor;
